import React, { useState, useEffect, useCallback } from 'react';
import { voiceFlowsAPI, leadsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Phone, PhoneCall, FileText, Loader2, Sparkles, ChevronRight, User, Building2, Zap, Target, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const FLOW_ICONS = { cold_intro: Phone, follow_up: RotateCcw, demo_scheduling: Target, objection_handling: Zap, closing: Sparkles };

export default function VoiceCallFlows() {
  const [flowTypes, setFlowTypes] = useState([]);
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState('');
  const [selectedFlow, setSelectedFlow] = useState('cold_intro');
  const [generatedFlow, setGeneratedFlow] = useState(null);
  const [leadFlows, setLeadFlows] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [typesRes, leadsRes] = await Promise.all([
        voiceFlowsAPI.getTypes(),
        leadsAPI.getAll({ limit: 50 }),
      ]);
      const typesData = typesRes.data;
      const flowTypesArr = Array.isArray(typesData)
        ? typesData
        : (typesData?.flow_types || typesData?.types || []);
      setFlowTypes(Array.isArray(flowTypesArr) ? flowTypesArr : []);
      const leadsData = Array.isArray(leadsRes.data) ? leadsRes.data : leadsRes.data?.leads || [];
      setLeads(leadsData);
      if (leadsData.length > 0) setSelectedLead(leadsData[0].id);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (selectedLead) {
      voiceFlowsAPI.getLeadFlows(selectedLead).then(res => {
        const d = res.data;
        const arr = Array.isArray(d) ? d : (d?.flows || []);
        setLeadFlows(Array.isArray(arr) ? arr : []);
      }).catch(() => {});
    }
  }, [selectedLead]);

  const generateFlow = async () => {
    if (!selectedLead) { toast.error('Select a lead first'); return; }
    setGenerating(true);
    try {
      const res = await voiceFlowsAPI.generate(selectedLead, selectedFlow);
      setGeneratedFlow(res.data);
      setLeadFlows(prev => [res.data, ...prev]);
      toast.success('Call flow generated');
    } catch { toast.error('Failed to generate flow'); }
    finally { setGenerating(false); }
  };

  const currentLead = leads.find(l => l.id === selectedLead);

  if (loading) return (
    <div className="space-y-4 p-6" data-testid="voice-flows-skeleton">
      <div className="h-7 w-48 bg-muted rounded animate-pulse" />
      <div className="h-4 w-64 bg-muted/60 rounded animate-pulse" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="p-5 rounded-lg border space-y-3">
            <div className="h-5 w-40 bg-muted rounded animate-pulse" />
            <div className="h-3 w-full bg-muted/40 rounded animate-pulse" />
            <div className="h-3 w-2/3 bg-muted/40 rounded animate-pulse" />
            <div className="h-8 w-24 bg-muted/60 rounded animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="voice-flows-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" data-testid="voice-flows-title">Voice Call Flows</h1>
        <p className="text-muted-foreground text-sm">Context-aware call scripts powered by Brain intelligence</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Generator Panel */}
        <Card className="lg:col-span-1" data-testid="flow-generator-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Sparkles className="h-4 w-4 text-cyan-500" /> Generate Flow</CardTitle>
            <CardDescription>Select a lead and flow type</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Lead</label>
              <Select value={selectedLead} onValueChange={setSelectedLead}>
                <SelectTrigger data-testid="flow-lead-select"><SelectValue placeholder="Select lead..." /></SelectTrigger>
                <SelectContent>
                  {leads.map(l => (
                    <SelectItem key={l.id} value={l.id}>
                      <span className="flex items-center gap-2">
                        <User className="h-3 w-3" /> {l.name} {l.ai_score ? `(${l.ai_score})` : ''}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {currentLead && (
              <div className="p-3 rounded-lg bg-muted/50 text-xs space-y-1" data-testid="lead-context-info">
                <div className="flex items-center gap-2"><Building2 className="h-3 w-3" /> {currentLead.company || 'N/A'}</div>
                <div className="flex items-center gap-2"><Zap className="h-3 w-3" /> AI Score: {currentLead.ai_score || 'N/A'}</div>
                <div className="flex items-center gap-2"><Target className="h-3 w-3" /> {currentLead.ai_qualification || currentLead.status || 'N/A'}</div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium mb-1 block">Flow Type</label>
              <Select value={selectedFlow} onValueChange={setSelectedFlow}>
                <SelectTrigger data-testid="flow-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {flowTypes.map(ft => {
                    const Icon = FLOW_ICONS[ft.id] || Phone;
                    return (
                      <SelectItem key={ft.id} value={ft.id}>
                        <span className="flex items-center gap-2"><Icon className="h-3 w-3" /> {ft.name}</span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <Button className="w-full" onClick={generateFlow} disabled={generating || !selectedLead} data-testid="generate-flow-btn">
              {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PhoneCall className="h-4 w-4 mr-2" />}
              Generate Script
            </Button>
          </CardContent>
        </Card>

        {/* Generated Flow */}
        <Card className="lg:col-span-2" data-testid="flow-result-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Call Script</CardTitle>
            {generatedFlow && <CardDescription>{generatedFlow.flow_name} — {generatedFlow.lead_context?.name}</CardDescription>}
          </CardHeader>
          <CardContent>
            {!generatedFlow ? (
              <div className="text-center py-12 text-muted-foreground text-sm">
                <Phone className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p>Select a lead and generate a call flow to see the script</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-xs">{generatedFlow.flow_name}</Badge>
                  <Badge variant="secondary" className="text-xs">Score: {generatedFlow.lead_context?.ai_score}</Badge>
                  {generatedFlow.templates_applied > 0 && (
                    <Badge className="text-xs bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400 border-0">
                      <Sparkles className="h-3 w-3 mr-1" /> {generatedFlow.templates_applied} templates
                    </Badge>
                  )}
                </div>

                <div className="space-y-3">
                  {generatedFlow.stages?.map((stage, i) => {
                    const script = generatedFlow.scripts?.[stage];
                    if (!script) return null;
                    return (
                      <div key={stage} className="border rounded-lg p-3" data-testid={`flow-stage-${stage}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-bold">{i + 1}</span>
                          <span className="text-sm font-medium capitalize">{stage.replace(/_/g, ' ')}</span>
                          <ChevronRight className="h-3 w-3 text-muted-foreground" />
                        </div>
                        {typeof script === 'string' ? (
                          <p className="text-sm text-muted-foreground leading-relaxed pl-7">{script}</p>
                        ) : (
                          <div className="pl-7 space-y-2">
                            {Object.entries(script).map(([key, val]) => (
                              <div key={key}>
                                <span className="text-xs font-medium text-muted-foreground uppercase">{key.replace(/_/g, ' ')}:</span>
                                <p className="text-sm mt-0.5">{val}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Previous Flows */}
      {leadFlows.length > 0 && (
        <Card data-testid="previous-flows-card">
          <CardHeader>
            <CardTitle className="text-base">Previous Flows for This Lead</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {leadFlows.slice(0, 6).map(flow => {
                const Icon = FLOW_ICONS[flow.flow_type] || Phone;
                return (
                  <button key={flow.id} onClick={() => setGeneratedFlow(flow)} className="text-left p-3 rounded-lg border hover:border-primary/50 transition-colors">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{flow.flow_name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{new Date(flow.created_at).toLocaleDateString()}</p>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
